import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const OnboardingTutorial = ({ isOpen, onClose, onComplete }) => {
  const { userData } = useAuth();
  const { t, currentLanguage } = useLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [showSkipOption, setShowSkipOption] = useState(false);

  const tutorialSteps = useMemo(() => [
    {
      id: 'welcome',
      title: t('tutorial.welcome.title'),
      description: t('tutorial.welcome.description'),
      content: (
        <div className="text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <p className="text-gray-600 mb-4">
            {t('tutorial.welcome.content.setup')}
          </p>
          <p className="text-sm text-gray-500">
            {t('tutorial.welcome.content.skip')}
          </p>
        </div>
      ),
      position: 'center'
    },
    {
      id: 'dashboard-overview',
      title: t('tutorial.dashboardOverview.title'),
      description: t('tutorial.dashboardOverview.description'),
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-blue-800">{t('tutorial.dashboardOverview.properties')}</span>
              </div>
              <p className="text-xs text-blue-600 mt-1">{t('tutorial.dashboardOverview.propertiesDesc')}</p>
            </div>
            <div className="p-3 bg-yellow-50 rounded-lg border border-yellow-200">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-yellow-800">{t('tutorial.dashboardOverview.requests')}</span>
              </div>
              <p className="text-xs text-yellow-600 mt-1">{t('tutorial.dashboardOverview.requestsDesc')}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-green-800">{t('tutorial.dashboardOverview.expenses')}</span>
              </div>
              <p className="text-xs text-green-600 mt-1">{t('tutorial.dashboardOverview.expensesDesc')}</p>
            </div>
            <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
              <div className="flex items-center space-x-2">
                <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                  <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
                <span className="text-sm font-medium text-purple-800">{t('tutorial.dashboardOverview.tenants')}</span>
              </div>
              <p className="text-xs text-purple-600 mt-1">{t('tutorial.dashboardOverview.tenantsDesc')}</p>
            </div>
          </div>
        </div>
      ),
      position: 'center'
    },
    {
      id: 'navigation',
      title: t('tutorial.navigation.title'),
      description: t('tutorial.navigation.description'),
      content: (
        <div className="space-y-4">
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <h4 className="font-medium text-gray-800 mb-3">{t('tutorial.navigation.sections.title')}</h4>
            <div className="space-y-2">
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-blue-500 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                  </svg>
                </div>
                <span className="text-sm text-gray-700">{t('tutorial.navigation.sections.dashboard')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-green-500 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                </div>
                <span className="text-sm text-gray-700">{t('tutorial.navigation.sections.properties')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-purple-500 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                  </svg>
                </div>
                <span className="text-sm text-gray-700">{t('tutorial.navigation.sections.tenants')}</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-6 h-6 bg-yellow-500 rounded flex items-center justify-center">
                  <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <span className="text-sm text-gray-700">{t('tutorial.navigation.sections.tickets')}</span>
              </div>
            </div>
          </div>
          <p className="text-sm text-gray-600">
            <strong>{t('tutorial.navigation.tip.label')}:</strong> {t('tutorial.navigation.tip.content')}
          </p>
        </div>
      ),
      position: 'center'
    },
    {
      id: 'properties',
      title: t('tutorial.properties.title'),
      description: t('tutorial.properties.description'),
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="font-medium text-blue-800 mb-3">{t('tutorial.properties.adding.title')}</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-blue-700">
              <li>{t('tutorial.properties.adding.step1')}</li>
              <li>{t('tutorial.properties.adding.step2')}</li>
              <li>{t('tutorial.properties.adding.step3')}</li>
            </ol>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <h4 className="font-medium text-green-800 mb-3">{t('tutorial.properties.management.title')}</h4>
            <ul className="space-y-2 text-sm text-green-700">
              <li>• {t('tutorial.properties.management.feature1')}</li>
              <li>• {t('tutorial.properties.management.feature2')}</li>
              <li>• {t('tutorial.properties.management.feature3')}</li>
              <li>• {t('tutorial.properties.management.feature4')}</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            <strong>{t('tutorial.properties.proTip.label')}:</strong> {t('tutorial.properties.proTip.content')}
          </p>
        </div>
      ),
      position: 'center'
    },
    {
      id: 'tenants',
      title: t('tutorial.tenants.title'),
      description: t('tutorial.tenants.description'),
      content: (
        <div className="space-y-4">
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <h4 className="font-medium text-purple-800 mb-3">{t('tutorial.tenants.creating.title')}</h4>
            <ol className="list-decimal list-inside space-y-2 text-sm text-purple-700">
              <li>{t('tutorial.tenants.creating.step1')}</li>
              <li>{t('tutorial.tenants.creating.step2')}</li>
              <li>{t('tutorial.tenants.creating.step3')}</li>
              <li>{t('tutorial.tenants.creating.step4')}</li>
              <li>{t('tutorial.tenants.creating.step5')}</li>
            </ol>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="font-medium text-blue-800 mb-3">{t('tutorial.tenants.features.title')}</h4>
            <ul className="space-y-2 text-sm text-blue-700">
              <li>• {t('tutorial.tenants.features.feature1')}</li>
              <li>• {t('tutorial.tenants.features.feature2')}</li>
              <li>• {t('tutorial.tenants.features.feature3')}</li>
              <li>• {t('tutorial.tenants.features.feature4')}</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            <strong>{t('tutorial.tenants.note.label')}:</strong> {t('tutorial.tenants.note.content')}
          </p>
        </div>
      ),
      position: 'center'
    },
    {
      id: 'maintenance',
      title: t('tutorial.maintenance.title'),
      description: t('tutorial.maintenance.description'),
      content: (
        <div className="space-y-4">
          <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200">
            <h4 className="font-medium text-yellow-800 mb-3">{t('tutorial.maintenance.tracking.title')}</h4>
            <div className="grid grid-cols-2 gap-3 text-sm text-yellow-700">
              <div>
                <span className="inline-block w-3 h-3 bg-amber-500 rounded-full mr-2"></span>
                <span>{t('tutorial.maintenance.tracking.open')}</span>
              </div>
              <div>
                <span className="inline-block w-3 h-3 bg-blue-500 rounded-full mr-2"></span>
                <span>{t('tutorial.maintenance.tracking.inProgress')}</span>
              </div>
              <div>
                <span className="inline-block w-3 h-3 bg-green-500 rounded-full mr-2"></span>
                <span>{t('tutorial.maintenance.tracking.completed')}</span>
              </div>
              <div>
                <span className="inline-block w-3 h-3 bg-gray-500 rounded-full mr-2"></span>
                <span>{t('tutorial.maintenance.tracking.closed')}</span>
              </div>
            </div>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <h4 className="font-medium text-green-800 mb-3">{t('tutorial.maintenance.managing.title')}</h4>
            <ul className="space-y-2 text-sm text-green-700">
              <li>• {t('tutorial.maintenance.managing.feature1')}</li>
              <li>• {t('tutorial.maintenance.managing.feature2')}</li>
              <li>• {t('tutorial.maintenance.managing.feature3')}</li>
              <li>• {t('tutorial.maintenance.managing.feature4')}</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            <strong>{t('tutorial.maintenance.tip.label')}:</strong> {t('tutorial.maintenance.tip.content')}
          </p>
        </div>
      ),
      position: 'center'
    },
    {
      id: 'service-providers',
      title: t('tutorial.serviceProviders.title'),
      description: t('tutorial.serviceProviders.description'),
      content: (
        <div className="space-y-4">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="font-medium text-blue-800 mb-3">{t('tutorial.serviceProviders.finding.title')}</h4>
            <ul className="space-y-2 text-sm text-blue-700">
              <li>• {t('tutorial.serviceProviders.finding.feature1')}</li>
              <li>• {t('tutorial.serviceProviders.finding.feature2')}</li>
              <li>• {t('tutorial.serviceProviders.finding.feature3')}</li>
              <li>• {t('tutorial.serviceProviders.finding.feature4')}</li>
            </ul>
          </div>
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <h4 className="font-medium text-green-800 mb-3">{t('tutorial.serviceProviders.working.title')}</h4>
            <ul className="space-y-2 text-sm text-green-700">
              <li>• {t('tutorial.serviceProviders.working.feature1')}</li>
              <li>• {t('tutorial.serviceProviders.working.feature2')}</li>
              <li>• {t('tutorial.serviceProviders.working.feature3')}</li>
              <li>• {t('tutorial.serviceProviders.working.feature4')}</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            <strong>{t('tutorial.serviceProviders.benefit.label')}:</strong> {t('tutorial.serviceProviders.benefit.content')}
          </p>
        </div>
      ),
      position: 'center'
    },
    {
      id: 'expenses',
      title: t('tutorial.expenses.title'),
      description: t('tutorial.expenses.description'),
      content: (
        <div className="space-y-4">
          <div className="bg-green-50 rounded-lg p-4 border border-green-200">
            <h4 className="font-medium text-green-800 mb-3">{t('tutorial.expenses.categories.title')}</h4>
            <div className="grid grid-cols-2 gap-3 text-sm text-green-700">
              <div>• {t('tutorial.expenses.categories.maintenance')}</div>
              <div>• {t('tutorial.expenses.categories.management')}</div>
              <div>• {t('tutorial.expenses.categories.insurance')}</div>
              <div>• {t('tutorial.expenses.categories.utilities')}</div>
            </div>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="font-medium text-blue-800 mb-3">{t('tutorial.expenses.insights.title')}</h4>
            <ul className="space-y-2 text-sm text-blue-700">
              <li>• {t('tutorial.expenses.insights.feature1')}</li>
              <li>• {t('tutorial.expenses.insights.feature2')}</li>
              <li>• {t('tutorial.expenses.insights.feature3')}</li>
              <li>• {t('tutorial.expenses.insights.feature4')}</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            <strong>{t('tutorial.expenses.goal.label')}:</strong> {t('tutorial.expenses.goal.content')}
          </p>
        </div>
      ),
      position: 'center'
    },
    {
      id: 'communication',
      title: t('tutorial.communication.title'),
      description: t('tutorial.communication.description'),
      content: (
        <div className="space-y-4">
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
            <h4 className="font-medium text-purple-800 mb-3">{t('tutorial.communication.features.title')}</h4>
            <ul className="space-y-2 text-sm text-purple-700">
              <li>• {t('tutorial.communication.features.feature1')}</li>
              <li>• {t('tutorial.communication.features.feature2')}</li>
              <li>• {t('tutorial.communication.features.feature3')}</li>
              <li>• {t('tutorial.communication.features.feature4')}</li>
            </ul>
          </div>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="font-medium text-blue-800 mb-3">{t('tutorial.communication.bestPractices.title')}</h4>
            <ul className="space-y-2 text-sm text-blue-700">
              <li>• {t('tutorial.communication.bestPractices.feature1')}</li>
              <li>• {t('tutorial.communication.bestPractices.feature2')}</li>
              <li>• {t('tutorial.communication.bestPractices.feature3')}</li>
              <li>• {t('tutorial.communication.bestPractices.feature4')}</li>
            </ul>
          </div>
          <p className="text-sm text-gray-600">
            <strong>{t('tutorial.communication.advantage.label')}:</strong> {t('tutorial.communication.advantage.content')}
          </p>
        </div>
      ),
      position: 'center'
    },
    {
      id: 'completion',
      title: t('tutorial.completion.title'),
      description: t('tutorial.completion.description'),
      content: (
        <div className="text-center">
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">{t('tutorial.completion.ready.title')}</h3>
          <p className="text-gray-600 mb-4">
            {t('tutorial.completion.ready.description')}
          </p>
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
            <h4 className="font-medium text-blue-800 mb-2">{t('tutorial.completion.nextSteps.title')}</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• {t('tutorial.completion.nextSteps.step1')}</li>
              <li>• {t('tutorial.completion.nextSteps.step2')}</li>
              <li>• {t('tutorial.completion.nextSteps.step3')}</li>
              <li>• {t('tutorial.completion.nextSteps.step4')}</li>
            </ul>
          </div>
          <p className="text-sm text-gray-500 mt-4">
            {t('tutorial.completion.help.text')}
          </p>
        </div>
      ),
      position: 'center'
    }
  ], [t, currentLanguage]);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
      setShowSkipOption(false);
      
      // Show skip option after 3 seconds
      const timer = setTimeout(() => setShowSkipOption(true), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    if (onComplete) {
      onComplete();
    }
    onClose();
  };

  const handleComplete = () => {
    // Mark tutorial as completed in user data
    if (userData) {
      // You can add logic here to mark the tutorial as completed
      // For now, we'll just close it
    }
    
    if (onComplete) {
      onComplete();
    }
    onClose();
  };

  const currentTutorialStep = tutorialSteps[currentStep];

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-6 border w-full max-w-2xl shadow-2xl rounded-2xl bg-white">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-800">{t('tutorial.header.title')}</h2>
              <p className="text-sm text-gray-500">{t('tutorial.header.step', { current: currentStep + 1, total: tutorialSteps.length })}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors duration-200"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Progress Bar */}
        <div className="mb-6">
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${((currentStep + 1) / tutorialSteps.length) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Content */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            {currentTutorialStep.title}
          </h3>
          <p className="text-gray-600 mb-4">
            {currentTutorialStep.description}
          </p>
          <div className="min-h-[200px]">
            {currentTutorialStep.content}
          </div>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-between pt-4 border-t border-gray-200">
          <div className="flex items-center space-x-3">
            {currentStep > 0 && (
              <button
                onClick={handlePrevious}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 flex items-center space-x-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>{t('previous')}</span>
              </button>
            )}
            
            {showSkipOption && (
              <button
                onClick={handleSkip}
                className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors duration-200"
              >
                {t('skip')}
              </button>
            )}
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={handleComplete}
              className="px-4 py-2 text-sm font-medium text-gray-500 hover:text-gray-700 transition-colors duration-200"
            >
              {t('close')}
            </button>
            
            <button
              onClick={handleNext}
              className="px-6 py-2 text-sm font-medium text-white bg-green-500 rounded-lg hover:bg-green-600 transition-colors duration-200 flex items-center space-x-2"
            >
              <span>{currentStep === tutorialSteps.length - 1 ? t('finish') : t('next')}</span>
              {currentStep < tutorialSteps.length - 1 && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTutorial;
